import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CircleDot, FileText, Plus, RotateCw, Search } from 'lucide-react';
import { IssueState, type IssueDto } from '@aiai/shared';
import { fetchMyOverview, fetchMyPrototypes, searchRunLogs } from '../api';
import { qk } from '../api/queryKeys';
import { newIssueHref } from '../lib/projectNav';
import { workspacePath } from '../lib/workspacePath';
import { useWorkspacePath } from '../hooks/useWorkspacePath';
import { useDebounce } from '../hooks/useDebounce';
import { useFeedback } from './FeedbackContext';
import { useLaunchMission } from './LaunchMissionContext';

function issueStatusLabel(issue: IssueDto): string {
  if (issue.state === 'building') return 'Running';
  if (issue.state === 'proposed') return 'Awaiting approval';
  if (issue.state === 'review') return 'Awaiting review';
  return 'Open';
}

interface JumpItem {
  key: string;
  label: string;
  hint: string;
  /** Either a route to navigate to, or an action to run instead (e.g. open a modal). */
  to?: string;
  action?: () => void;
  logText?: string;
}

/** ⌘K / Ctrl+K jump-and-launch palette. Client-side only, cached queries. */
export function JumpPalette() {
  const isMac = /Mac/i.test(navigator.platform);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  const navigate = useNavigate();
  const { wp } = useWorkspacePath();
  const inputRef = useRef<HTMLInputElement>(null);
  const { openFeedback } = useFeedback();
  const { openLaunch } = useLaunchMission();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setSelected(0);
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  const debouncedQuery = useDebounce(query, 400);

  // The palette searches across EVERY workspace the user belongs to, via the
  // cross-workspace /me feeds (one overview call replaces the old per-project
  // issue fan-out). Each result links into its own workspace by slug.
  const overviewQuery = useQuery({ queryKey: qk.myOverview, queryFn: fetchMyOverview, enabled: open, staleTime: 10_000 });
  const prototypesQuery = useQuery({
    queryKey: qk.myPrototypes,
    queryFn: fetchMyPrototypes,
    enabled: open,
  });
  const logSearchQuery = useQuery({
    queryKey: qk.logSearch(debouncedQuery),
    queryFn: () => searchRunLogs(debouncedQuery, 8),
    enabled: open && debouncedQuery.trim().length >= 2,
    staleTime: 30_000,
  });

  const items = useMemo<JumpItem[]>(() => {
    const projects = overviewQuery.data?.projects ?? [];
    const issues = overviewQuery.data?.issues ?? [];
    const runs = overviewQuery.data?.activeRuns ?? [];
    const prototypes = prototypesQuery.data ?? [];
    const projectNameById = new Map(projects.map((p) => [p.id, p.name]));
    const openIssues: JumpItem[] = issues
      .filter((issue) => issue.state !== IssueState.Closed)
      .map((issue) => {
        const projectName = projectNameById.get(issue.projectId) ?? '';
        const statusLabel = issueStatusLabel(issue);
        return {
          key: `issue-${issue.id}`,
          label: issue.code ? `${issue.code} ${issue.title}` : issue.title,
          hint: `${issue.workspace.name}${projectName ? ` · ${projectName}` : ''} — ${statusLabel}`,
          to: workspacePath(issue.workspace.slug, `/issues/${issue.code ?? issue.id}`),
        };
      });
    return [
      ...runs.map((r) => ({
        key: `run-${r.id}`,
        label: r.issueCode ? `${r.issueCode} ${r.issueTitle}` : r.issueTitle,
        hint: `${r.workspace.name} — Running now`,
        to: workspacePath(r.workspace.slug, `/runs/${r.id}`),
      })),
      ...openIssues,
      ...projects.map((p) => ({
        key: `proj-${p.id}`,
        label: p.name,
        hint: `${p.workspace.name} — Project`,
        to: workspacePath(p.workspace.slug, `/projects/${p.code ?? p.id}`),
      })),
      ...prototypes.map((p) => ({
        key: `proto-${p.id}`,
        label: p.name,
        hint: `${p.workspace.name} — Prototype`,
        to: workspacePath(p.workspace.slug, `/prototypes/${p.code ?? p.id}`),
      })),
      ...projects.map((p) => ({
        key: `new-issue-${p.id}`,
        label: `New issue in ${p.name}`,
        hint: 'Create',
        to: workspacePath(p.workspace.slug, newIssueHref(p.kind, p.code ?? p.id)),
      })),
      ...projects.map((p) => ({
        key: `new-loop-${p.id}`,
        label: `New loop in ${p.name}`,
        hint: 'Create',
        to: workspacePath(p.workspace.slug, `/projects/${p.code ?? p.id}/loops/new`),
      })),
      // Create-a-prototype needs a workspace to target, so it routes to the
      // global Prototypes page where the create flow resolves one.
      { key: 'new-prototype', label: 'New prototype', hint: 'Create', to: '/prototypes' },
      { key: 'launch-mission', label: 'Launch mission…', hint: 'Create', action: openLaunch },
      { key: 'feedback', label: 'Send feedback to aiai-loop', hint: 'Create', action: openFeedback },
    ];
  }, [overviewQuery.data, prototypesQuery.data, openFeedback, openLaunch]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const regular = q ? items.filter((i) => i.label.toLowerCase().includes(q)) : items;
    const logItems: JumpItem[] = (logSearchQuery.data ?? []).map((hit) => ({
      key: `log-${hit.runId}-${hit.logSeq}`,
      label: hit.issueTitle || 'Untitled issue',
      hint: 'Log',
      to: wp(`/runs/${hit.runId}`),
      logText: hit.logText,
    }));
    return [...regular, ...logItems].slice(0, 16);
  }, [items, query, logSearchQuery.data, wp]);

  const go = (item: JumpItem | undefined) => {
    if (!item) return;
    setOpen(false);
    if (item.action) {
      item.action();
    } else if (item.to) {
      navigate(item.to);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Jump to"
        className="flex h-8 w-8 items-center justify-center gap-2 rounded-control border border-line-strong bg-surface-1 text-xs text-ink-faint transition-colors hover:bg-surface-2 hover:text-ink-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-signal/60 lg:w-auto lg:px-2.5"
      >
        <Search className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
        <span className="hidden lg:inline">Jump to…</span>
        <kbd className="hidden rounded-[4px] border border-b-2 border-line px-1 font-mono text-2xs leading-none text-ink-faint lg:inline-block">{isMac ? '⌘K' : 'Ctrl K'}</kbd>
      </button>
      {open &&
        createPortal(
          <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-[12vh]">
            <div
              className="fixed inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setOpen(false)}
              aria-hidden="true"
            />
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Jump to"
              className="card relative z-10 w-full max-w-md animate-fade-in overflow-hidden shadow-2xl"
            >
              <div className="flex items-center gap-2 border-b border-line px-3.5 py-2.5">
                <Search className="h-4 w-4 text-ink-faint" aria-hidden="true" />
                <input
                  ref={inputRef}
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setSelected(0);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      setSelected((s) =>
                        Math.min(s + 1, Math.max(filtered.length - 1, 0)),
                      );
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      setSelected((s) => Math.max(s - 1, 0));
                    } else if (e.key === 'Enter') {
                      e.preventDefault();
                      go(filtered[selected]);
                    }
                  }}
                  placeholder="Jump to a project, run… or start something"
                  aria-label="Jump to"
                  className="w-full bg-transparent text-sm text-ink placeholder:text-ink-faint focus:outline-none"
                />
              </div>
              <ul className="max-h-72 overflow-y-auto p-1">
                {filtered.length === 0 && (
                  <li className="px-3 py-4 text-center text-xs text-ink-faint">No matches.</li>
                )}
                {filtered.map((item, i) => (
                  <li key={item.key}>
                    <button
                      type="button"
                      onClick={() => go(item)}
                      onMouseEnter={() => setSelected(i)}
                      className={`flex w-full items-center justify-between gap-3 rounded-md px-2.5 py-2 text-left text-sm transition-colors ${
                        i === selected ? 'bg-surface-2 text-ink' : 'text-ink-muted'
                      }`}
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        {item.hint === 'Create' ? (
                          <Plus className="h-3.5 w-3.5 flex-none text-ink-faint" aria-hidden="true" />
                        ) : item.hint === 'Log' ? (
                          <FileText className="h-3.5 w-3.5 flex-none text-ink-faint" aria-hidden="true" />
                        ) : item.key.startsWith('issue-') ? (
                          <CircleDot className="h-3.5 w-3.5 flex-none text-ink-faint" aria-hidden="true" />
                        ) : (
                          <RotateCw className="h-3.5 w-3.5 flex-none text-ink-faint" aria-hidden="true" />
                        )}
                        <span className="flex min-w-0 flex-col">
                          <span className="truncate">{item.label}</span>
                          {item.logText && (
                            <span className="truncate font-mono text-2xs text-ink-faint">{item.logText}</span>
                          )}
                        </span>
                      </span>
                      <span className="flex-none text-2xs uppercase tracking-wide text-ink-faint">
                        {item.hint}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
